import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBell, faCalendarPlus, faCalendarCheck, faCalendarXmark, faBan,
    faMoneyBillWave, faCakeCandles, faStar, faEye,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useBusinessContext } from '../../../context/BusinessContext';
import { notificationTargetMenu } from '../../util/Utils';
import { useNotifications } from '../../../hooks/queries';
import { useOpenNotification } from '../../../hooks/useOpenNotification';
import LoadingComponent from '../../commons/LoadingComponent';
import { Notification } from '../../../model/notification/Notification';
import './NotificationsComponent.css';

/** Category → icon + color for the row badge. */
function categoryVisual(category?: string): { icon: IconDefinition; color: string } {
    switch (category) {
        case 'APPOINTMENT_BOOKED': return { icon: faCalendarPlus, color: '#3b82f6' };
        case 'APPOINTMENT_CONFIRMED': return { icon: faCalendarCheck, color: '#22c55e' };
        case 'APPOINTMENT_CANCELLED': return { icon: faCalendarXmark, color: '#ef4444' };
        case 'APPOINTMENT_DECLINED': return { icon: faBan, color: '#ef4444' };
        case 'APPOINTMENT_REMINDER': return { icon: faCalendarCheck, color: '#3b82f6' };
        case 'PAYMENT_RECEIVED': return { icon: faMoneyBillWave, color: '#22c55e' };
        case 'BIRTHDAY': return { icon: faCakeCandles, color: '#ec4899' };
        case 'REVIEW_REQUEST': return { icon: faStar, color: '#f59e0b' };
        default: return { icon: faBell, color: '#7c6cf0' };
    }
}

/** Compact relative time ("há 5 min", "há 2 h", or a date). */
function relativeTime(iso: string | undefined, t: (k: string, o?: Record<string, unknown>) => string): string {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return '';
    const diffMin = Math.round((Date.now() - then) / 60000);
    if (diffMin < 1) return t('notifications.now');
    if (diffMin < 60) return t('notifications.minAgo', { count: diffMin });
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return t('notifications.hourAgo', { count: diffH });
    return new Date(iso).toLocaleDateString();
}

const NotificationsComponent: React.FC = () => {
    const { t } = useTranslation();
    const { selectedBusiness } = useBusinessContext();
    const open = useOpenNotification();
    const businessId = selectedBusiness?.id ? String(selectedBusiness.id) : '';

    const query = useNotifications(businessId);
    const notifications: Notification[] = query.data ?? [];
    const loading = query.isLoading;

    if (loading) return <LoadingComponent />;

    return (
        <div className="notifications-page" data-cy="notifications-page">
            <div className="list-card">
                <div className="list-header">
                    <h5 className="mb-0">{t('notifications.title')}</h5>
                </div>

                {notifications.length === 0 ? (
                    <div className="empty-hint">{t('notifications.empty')}</div>
                ) : (
                    <div className="notif-table-wrap">
                        <table className="notif-table" data-cy="notif-table">
                            <thead>
                                <tr>
                                    <th className="notif-table__icon-col" />
                                    <th>{t('notifications.colNotification')}</th>
                                    <th>{t('notifications.colWhen')}</th>
                                    <th className="notif-table__actions-col">{t('notifications.colActions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notifications.map((n) => {
                                    const v = categoryVisual(n.category);
                                    const clickable = !!notificationTargetMenu(n.category);
                                    return (
                                        <tr key={n.id ?? n.title} className={`notif-row${!n.read ? ' notif-row--unread' : ''}`} data-cy="notif-row">
                                            <td className="notif-table__icon-col">
                                                <span className="notif-row__icon" style={{ color: v.color, background: `${v.color}1a` }}>
                                                    <FontAwesomeIcon icon={v.icon} />
                                                </span>
                                            </td>
                                            <td>
                                                <div className="notif-row__title">{n.title}</div>
                                                {n.body && <div className="notif-row__sub">{n.body}</div>}
                                            </td>
                                            <td className="notif-row__when">{relativeTime(n.createdAt, t)}</td>
                                            <td className="notif-table__actions-col">
                                                {clickable && (
                                                    <button
                                                        type="button"
                                                        className="notif-row__open"
                                                        onClick={() => open(n)}
                                                        title={t('notifications.openTitle')}
                                                        aria-label={t('notifications.openTitle')}
                                                        data-cy="notif-open"
                                                    >
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NotificationsComponent;
